import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Loader as Loader2, Link as LinkIcon, Check, Play, Pause, Volume2, Mic, Maximize, Minimize } from 'lucide-react';
import { KioskService } from '../services/KioskService';
import ShaderBackground from '../components/ShaderBackground';

interface SharedData {
  submission_id: string;
  display_name: string;
  score: number | null;
  song_title: string | null;
  song_artist: string | null;
  song_cover_url: string | null;
  mixed_video_url: string | null;
  mixed_audio_url: string | null;
  recording_url: string | null;
  video_mux_status: string | null;
  recording_type: string | null;
  submitted_at: string;
  league: {
    id: string;
    slug: string;
    name: string;
    logo_url: string | null;
    primary_color: string | null;
    secondary_color: string | null;
    accent_color: string | null;
    background_image_url: string | null;
    sponsor_logos: string[];
    sponsor_links: string[];
  };
}

export const KioskShare: React.FC = () => {
  const { submissionId } = useParams<{ slug: string; submissionId: string }>();
  const [data, setData] = useState<SharedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    if (!submissionId) return;
    (async () => {
      try {
        const result = await KioskService.getSharedSubmission(submissionId);
        setData(result as SharedData);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [submissionId]);

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2500);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Loader2 className="w-8 h-8 animate-spin text-white/60" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <p className="text-white/60 text-lg">{error || 'Recording not found'}</p>
        </div>
      </div>
    );
  }

  const league = data.league;
  const primary = league.primary_color || '#f97316';
  const secondary = league.secondary_color || '#3b82f6';
  const accent = league.accent_color || primary;
  const sponsors = league.sponsor_logos || [];
  const sponsorLinks = league.sponsor_links || [];

  const videoReady = data.mixed_video_url && data.video_mux_status === 'completed';
  const isAudioRecording = data.recording_type === 'audio';
  const audioSrc = data.mixed_audio_url || data.recording_url;
  const audioReady = isAudioRecording && !!audioSrc;
  const downloadUrl = videoReady ? data.mixed_video_url! : audioReady ? audioSrc! : null;

  return (
    <div className="relative min-h-screen bg-slate-950 text-white">
      <div className="fixed inset-0 z-0 overflow-hidden">
        <ShaderBackground preset="aurora" primaryColor={primary} secondaryColor={secondary} />
      </div>
      <div className="fixed inset-0 z-0 bg-black/55" />

      {/* Toast notification */}
      <div
        className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
          toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-2 px-5 py-3 rounded-xl bg-green-600 text-white font-semibold shadow-2xl border border-green-400/30">
          <Check className="w-4 h-4" />
          Copied to clipboard
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header with league logo */}
        <header
          className="relative border-b border-white/10 shadow-2xl backdrop-blur-md"
          style={{ background: `linear-gradient(135deg, ${primary}E6, ${secondary}CC)` }}
        >
          <div className="absolute inset-0 bg-black/25 pointer-events-none" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {league.logo_url ? (
                <img
                  src={league.logo_url}
                  alt={league.name}
                  className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-contain bg-white/95 p-1.5 shadow-lg border border-white/20"
                />
              ) : (
                <div
                  className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center font-black text-xl shadow-lg border border-white/20"
                  style={{ backgroundColor: accent }}
                >
                  {(league.name || 'K').charAt(0)}
                </div>
              )}
              <div>
                <div className="text-xl sm:text-2xl font-black tracking-tight drop-shadow-lg">
                  {league.name}
                </div>
                <div className="text-sm text-white/80">{data.display_name}'s Performance</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur-sm transition shadow-lg text-sm"
              >
                <LinkIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Copy Link</span>
              </button>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  download
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur-sm transition shadow-lg text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download</span>
                </a>
              )}
            </div>
          </div>
        </header>

        {/* Main content - media player */}
        <main className="flex-1 flex items-center justify-center px-4 sm:px-8 py-8">
          <div className="w-full max-w-3xl">
            {videoReady ? (
              <VideoPlayerWithSponsors
                src={data.mixed_video_url!}
                poster={data.song_cover_url || undefined}
              />
            ) : audioReady ? (
              <AudioPlayer src={audioSrc!} accent={accent} hasMix={!!data.mixed_audio_url} coverUrl={data.song_cover_url} sponsors={sponsors} sponsorLinks={sponsorLinks} />
            ) : isAudioRecording ? (
              <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-white/40 mx-auto mb-4" />
                <p className="text-white/60 text-lg">Audio is being mixed. Check back shortly.</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-white/40 mx-auto mb-4" />
                <p className="text-white/60 text-lg">Video is still rendering. Check back shortly.</p>
              </div>
            )}
            {/* Song and score info */}
            {data.song_title && (
              <div className="mt-6 flex items-center gap-4 bg-white/5 backdrop-blur-md border border-white/15 rounded-xl p-4">
                {data.song_cover_url && (
                  <img
                    src={data.song_cover_url}
                    alt={data.song_title}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-lg truncate">{data.song_title}</div>
                  <div className="text-sm text-white/60 truncate">{data.song_artist}</div>
                </div>
                {data.score != null && (
                  <div className="text-right">
                    <div className="text-3xl font-black" style={{ color: accent }}>
                      {Number(data.score).toFixed(1)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Points</div>
                  </div>
                )}
              </div>
            )}

            {/* Sponsors - inline with content */}
            {sponsors.length > 0 && (
              <div className="mt-6 bg-white/5 backdrop-blur-md border border-white/15 rounded-xl p-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold text-center mb-4">
                  Sponsored by
                </div>
                <div className="flex items-center justify-center gap-6 sm:gap-8 flex-wrap">
                  {sponsors.map((logo, i) => {
                    const link = sponsorLinks[i];
                    const img = (
                      <img
                        src={logo}
                        alt={`Sponsor ${i + 1}`}
                        className="h-12 sm:h-16 md:h-20 object-contain opacity-90 hover:opacity-100 transition-opacity"
                      />
                    );
                    return link ? (
                      <a key={i} href={link} target="_blank" rel="noreferrer" className="shrink-0">
                        {img}
                      </a>
                    ) : (
                      <div key={i} className="shrink-0">{img}</div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="px-4 sm:px-8 py-4 border-t border-white/10 bg-black/40 backdrop-blur-md">
          <div className="max-w-5xl mx-auto text-center text-xs text-white/30">
            {league.name}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default KioskShare;

/* ── Sponsor strip reused in both video + audio players ── */
const SponsorStrip: React.FC<{ sponsors: string[]; sponsorLinks: string[] }> = ({ sponsors, sponsorLinks }) => {
  if (sponsors.length === 0) return null;
  return (
    <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap px-3 py-2">
      {sponsors.map((logo, i) => {
        const link = sponsorLinks[i];
        const img = (
          <img
            src={logo}
            alt={`Sponsor ${i + 1}`}
            className="h-12 w-12 sm:h-14 sm:w-14 object-contain opacity-90 hover:opacity-100 transition-opacity drop-shadow-lg"
          />
        );
        return link ? (
          <a key={i} href={link} target="_blank" rel="noreferrer" className="shrink-0">
            {img}
          </a>
        ) : (
          <div key={i} className="shrink-0">{img}</div>
        );
      })}
    </div>
  );
};

/* ── Video player with sponsor overlay that follows fullscreen ── */
const VideoPlayerWithSponsors: React.FC<{
  src: string;
  poster?: string;
}> = ({ src, poster }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await containerRef.current.requestFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  useEffect(() => {
    return () => clearTimeout(hideTimer.current);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative group rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black ${isFullscreen ? 'flex flex-col' : ''}`}
      onMouseMove={resetHideTimer}
      onTouchStart={resetHideTimer}
    >
      {/* Fullscreen toggle - top right */}
      <button
        onClick={toggleFullscreen}
        className={`absolute top-3 right-3 z-30 p-2 rounded-lg bg-black/50 hover:bg-black/70 text-white/80 hover:text-white transition-opacity duration-300 ${
          isFullscreen && !showControls ? 'opacity-0' : 'opacity-100 group-hover:opacity-100'
        }`}
      >
        {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
      </button>

      <video
        ref={videoRef}
        src={src}
        controls
        className={`w-full ${isFullscreen ? 'flex-1 min-h-0' : 'aspect-video'}`}
        poster={poster}
      />


    </div>
  );
};

const AudioPlayer: React.FC<{ src: string; accent: string; hasMix: boolean; coverUrl: string | null; sponsors: string[]; sponsorLinks: string[] }> = ({ src, accent, hasMix, coverUrl, sponsors, sponsorLinks }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play(); setPlaying(true); }
  };

  const handleTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    setProgress(el.currentTime);
    if (el.duration && !isNaN(el.duration)) setDuration(el.duration);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.currentTime = time;
    setProgress(time);
  };

  const fmt = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-8 sm:p-12">
      <div className="flex flex-col items-center">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="w-32 h-32 rounded-xl object-cover shadow-xl mb-6 border border-white/10" />
        ) : (
          <div
            className="w-28 h-28 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: `${accent}20`, border: `2px solid ${accent}` }}
          >
            {hasMix ? <Volume2 size={44} style={{ color: accent }} /> : <Mic size={44} style={{ color: accent }} />}
          </div>
        )}
        {!hasMix && <p className="text-xs text-white/40 mb-4">Vocals only</p>}

        <div className="w-full max-w-sm flex items-center gap-3 mb-2">
          <button
            onClick={toggle}
            className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition hover:scale-105 active:scale-95"
            style={{ backgroundColor: accent }}
          >
            {playing ? <Pause size={20} className="text-slate-900" fill="currentColor" /> : <Play size={20} className="text-slate-900 ml-0.5" fill="currentColor" />}
          </button>
          <div className="flex-1">
            <input
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={progress}
              onChange={handleSeek}
              className="w-full accent-white h-1.5 rounded-full appearance-none bg-white/20"
            />
            <div className="flex justify-between text-[11px] text-white/50 mt-1 tabular-nums">
              <span>{fmt(progress)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </div>
        </div>
      </div>
      {sponsors.length > 0 && (
        <div className="mt-6 pt-4 border-t border-white/10">
          <SponsorStrip sponsors={sponsors} sponsorLinks={sponsorLinks} />
        </div>
      )}
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => setDuration((e.target as HTMLAudioElement).duration)}
        className="hidden"
      />
    </div>
  );
};
